import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useContext, useCallback, useEffect, useState, Fragment, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/custom-theme-button';

import { InfiniteScroll } from '@confluence/infinite-scroll';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { FeedCardContext } from '../FeedCard/FeedCardContext.ts';

import type {
	AllActivityDropdownFeedCardAllActivityQuery as AllActivityDropdownFeedCardAllActivityQuery$data,
	AllActivityDropdownFeedCardAllActivityQueryVariables as AllActivityDropdownFeedCardAllActivityQuery$variables,
} from './__types__/AllActivityDropdownFeedCardAllActivityQuery.ts';
import { ActivityLineUpdateDetail } from './ActivityLineUpdateDetail.tsx';
import { AllActivityError } from './AllActivityError/index.ts';
import { ActivityLineUpdateDetailSkeleton } from './ActivityLineUpdateDetailSkeleton.tsx';
import {
	getSupportedUpdateType,
	getAvatarUrl,
	getUpdateContentUrl,
} from './activityLineHelpers.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const AllActivityDropdownFeedCardAllActivityQuery = graphql`
	query AllActivityDropdownFeedCardAllActivityQuery(
		$contentId: ID!
		$after: String
		$fromDate: String
	) {
		pageActivity(contentId: $contentId, after: $after, fromDate: $fromDate) {
			nodes {
				timestamp
				action
				actionSubject
				... on PageActivityEventCreatedPage {
					groupSize
					user {
						id
						displayName
						photos {
							value
							isPrimary
						}
					}
				}
				... on PageActivityEventUpdatedPage {
					pageVersion
					groupSize
					user {
						id
						displayName
						photos {
							value
							isPrimary
						}
					}
				}
				... on PageActivityEventCreatedComment {
					groupSize
					comment {
						id
						links {
							webui
						}
					}
					user {
						id
						displayName
						photos {
							value
							isPrimary
						}
					}
				}
				... on PageActivityEventStartedPage {
					pageVersion
					groupSize
					user {
						id
						displayName
						photos {
							value
							isPrimary
						}
						__typename
					}
					__typename
				}
				... on PageActivityEventSnapshottedPage {
					pageVersion
					groupSize
					user {
						id
						displayName
						photos {
							value
							isPrimary
						}
						__typename
					}
					__typename
				}
				... on PageActivityEventPublishedPage {
					pageVersion
					groupSize
					user {
						id
						displayName
						photos {
							value
							isPrimary
						}
						__typename
					}
					__typename
				}
			}
			pageInfo {
				endCursor
				hasNextPage
			}
		}
	}
`;
const i18n = defineMessages({
	otherActions: {
		id: 'feed.card.otherActions',
		defaultMessage: '{num} recent {num, plural, one {action} other {actions} }',
		description: 'Text on the action summary text for the other actions button',
	},
	otherActionsWithoutNumber: {
		id: 'feed.card.otherActionsWithoutNumber',
		defaultMessage: 'Recent actions',
		description:
			'Text on the action summary text for the other actions button when the number of recent actions is not shown',
	},
});

// Inline dialog component wraps this with 16px 24px padding by default,
// so we need to compensate for that with a negative margin
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionDialog = styled.div({
	width: '316px',
	maxHeight: '235px',
	overflowY: 'auto',
	overflowX: 'hidden',
	paddingLeft: token('space.200'),
	paddingRight: token('space.200'),
	paddingTop: token('space.200'),
	paddingBottom: token('space.025'),
});

const buttonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			marginRight: token('space.050'),
			fontWeight: token('font.weight.regular'),
			color: token('color.text.subtle'),
		},
		spinnerStyles,
	};
};

type Props = {
	contentUrl: string;
	contentId: string;
	recentActionsCount: number;
	shouldShowRecentActionsCountNumber?: boolean;
};

const AllActivityDropdownComponent: FC<Props> = ({
	contentUrl,
	contentId,
	recentActionsCount,
	shouldShowRecentActionsCountNumber = true,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	const [hovered, setHovered] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [nextPageError, setNextPageError] = useState<ApolloErrorInterop | null>();
	const thirtyDaysAgo = useMemo(() => {
		const currentDate = new Date();
		const date = new Date(currentDate.setDate(currentDate.getDate() - 30));
		return date.toISOString();
	}, []);

	const {
		data,
		error: firstPageError,
		loading,
		fetchMore,
	} = useQueryInterop<
		AllActivityDropdownFeedCardAllActivityQuery$data,
		AllActivityDropdownFeedCardAllActivityQuery$variables
	>(AllActivityDropdownFeedCardAllActivityQuery, {
		variables: { contentId, fromDate: thirtyDaysAgo },
		skip: !(dialogOpen || hovered),
	});
	const error = firstPageError || nextPageError;
	const hasNextPage = data?.pageActivity?.pageInfo?.hasNextPage;
	const endCursor = data?.pageActivity?.pageInfo?.endCursor;

	const onHover = useCallback(() => {
		if (!hovered) {
			setHovered(true);
		}
	}, [setHovered, hovered]);

	const toggleDialog = useCallback(() => {
		setDialogOpen((open) => !open);
	}, [setDialogOpen]);

	const closeDialog = useCallback(() => {
		setDialogOpen(false);
	}, [setDialogOpen]);
	const updates = data?.pageActivity?.nodes || [];
	useEffect(() => {
		if (dialogOpen) {
			fireAnalyticsEvent({
				type: 'sendTrackEvent',
				action: 'more',
				actionSubject: 'recentActions',
				actionSubjectId: 'feedCardRecentActionsPeak',
			});
		}
	}, [dialogOpen, fireAnalyticsEvent]);

	const handlePagination = useCallback(async () => {
		try {
			await fetchMore({
				variables: { contentId, after: endCursor, fromDate: thirtyDaysAgo },
				updateQuery: (prevResults, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResults;
					}
					fetchMoreResult.pageActivity.nodes = prevResults.pageActivity.nodes.concat(
						fetchMoreResult.pageActivity.nodes,
					);
					return fetchMoreResult;
				},
			});
		} catch (e) {
			setNextPageError(e);
		}
	}, [fetchMore, endCursor, contentId, thirtyDaysAgo]);

	const renderEndOfActivityList = () => {
		if (error) {
			return (
				<ErrorDisplay error={error}>
					<AllActivityError />
				</ErrorDisplay>
			);
		}

		if (!loading && hasNextPage && endCursor) {
			return (
				<InfiniteScroll
					onScrollTo={handlePagination}
					cursor={endCursor}
					observerOptions={{ root: null, threshold: 0.05 }}
				>
					<ActivityLineUpdateDetailSkeleton />
				</InfiniteScroll>
			);
		}

		return null;
	};

	const renderDialogBody = () => {
		if (loading) {
			return <ActivityLineUpdateDetailSkeleton />;
		}

		return (
			<Fragment>
				{updates.map((update) => {
					if (update) {
						const type = getSupportedUpdateType(update.actionSubject, update.action);
						if (type) {
							const userId = update.user?.id || null;
							const avatarUrl = getAvatarUrl(update.user?.photos || null);
							const name = update.user?.displayName || null;

							const actionTime = update.timestamp;
							const link = getUpdateContentUrl(update, contentUrl, contentId);

							return (
								<ActivityLineUpdateDetail
									key={`${type}-${userId}-${actionTime}`}
									type={type}
									actionTime={actionTime}
									userId={userId}
									avatarUrl={avatarUrl}
									userDisplayName={name}
									link={link}
									groupSize={update.groupSize}
								/>
							);
						}
					}

					return null;
				})}

				{renderEndOfActivityList()}
			</Fragment>
		);
	};

	const { Popup } = useChoreographerAssets();

	return (
		<Popup
			isOpen={dialogOpen}
			content={() => <ActionDialog>{renderDialogBody()}</ActionDialog>}
			onClose={closeDialog}
			placement="bottom-end"
			shouldRenderToParent
			trigger={(triggerProps) => (
				<Button
					onClick={toggleDialog}
					onMouseEnter={onHover}
					theme={buttonTheme}
					appearance="subtle-link"
					spacing="none"
					{...triggerProps}
				>
					<FormattedMessage
						{...(shouldShowRecentActionsCountNumber
							? i18n.otherActions
							: i18n.otherActionsWithoutNumber)}
						values={{ num: recentActionsCount }}
					/>
				</Button>
			)}
		/>
	);
};

export const AllActivityDropdown = withTransparentErrorBoundary({
	attribution: Attribution.SMARTS,
})(AllActivityDropdownComponent);
